import { z } from "zod";

/**
 * Per-model Vref data, transcribed from the CJP FOQA Users Guide appendix:
 *
 *   landing gross weight = default zero-fuel weight (per model) + fuel on board
 *   Vref = lookup(gross weight) from the per-model table
 *
 * The top table row for each model is "emergency landing only" weight; the second is max
 * landing weight. Callers interpolate between rows and clamp at the ends.
 *
 * SEED DATA, NOT SOURCE OF TRUTH: the live values are rows in `foqa_model_profiles`.
 * This constant exists to seed that table and to keep dev scripts runnable without a DB.
 * It lives in @ca/db rather than sms-worker so the seed can reach it without a cycle.
 */

export const FoqaWeightVrefEndpointsSchema = z.object({
	minWeightLb: z.number(),
	minWeightVref: z.number(),
	maxWeightLb: z.number(),
	maxWeightVref: z.number(),
});

export type WeightVrefEndpoints = z.infer<typeof FoqaWeightVrefEndpointsSchema>;

export const FoqaVrefProfileSchema = z.object({
	/** Default zero-fuel weight, lb. */
	zfwLb: z.number(),
	/** Gross weight (lb) → Vref (kt), descending by weight. */
	table: z.array(z.tuple([z.number(), z.number()])),
	/** Vmo (or Vne for a piston), KIAS — the AFM limit the max-flight-speed flag is relative to. */
	vneKt: z.number().optional(),
	/** Mmo — the AFM limit the max-flight-Mach flag is relative to. Absent on pistons. */
	mmo: z.number().optional(),
	vrKt: z.number().optional(),
	vswKt: z.number().optional(),
	vmxtKt: z.number().optional(),
	vs0Kt: z.number().optional(),
	vs1Kt: z.number().optional(),
	vxKt: z.number().optional(),
	vyKt: z.number().optional(),
	minTdFt: z.number().optional(),
	mrrTdFt: z.number().optional(),
	weightVref: FoqaWeightVrefEndpointsSchema.optional(),
});

export type AircraftVrefProfile = z.infer<typeof FoqaVrefProfileSchema>;

export const CJP_VREF_PROFILES: Record<string, AircraftVrefProfile> = {
	"mustang": {
		zfwLb: 5963,
		table: [[8645, 98], [8000, 94], [7500, 91], [7000, 88], [6500, 85], [6000, 82]],
		vneKt: 250,
		mmo: 0.63,
		vrKt: 90,
		vswKt: 82,
		vmxtKt: 25,
		minTdFt: 200,
		weightVref: {
			minWeightLb: 6000, minWeightVref: 82, maxWeightLb: 8645, maxWeightVref: 98,
		},
	},
	"cj1+": {
		zfwLb: 7523,
		table: [
			[10700, 113], [9900, 109], [9700, 108], [9500, 107], [9300, 106], [9100, 105],
			[8900, 103], [8700, 102], [8500, 101], [8000, 98], [7500, 95], [6500, 89],
		],
		vneKt: 263,
		mmo: 0.71,
		vrKt: 106,
		vswKt: 95,
		vmxtKt: 25,
		vs0Kt: 82,
		vs1Kt: 95,
		vxKt: 113,
		vyKt: 190,
		minTdFt: 200,
		weightVref: {
			minWeightLb: 6500, minWeightVref: 89, maxWeightLb: 10700, maxWeightVref: 113,
		},
	},
	"m2": {
		zfwLb: 7513,
		table: [
			[10700, 113], [9900, 109], [9700, 108], [9500, 107], [9300, 106], [9100, 105],
			[8900, 103], [8700, 102], [8500, 101], [8000, 98], [6500, 89],
		],
		vneKt: 263,
		mmo: 0.71,
		vrKt: 106,
		vswKt: 95,
		vmxtKt: 25,
		minTdFt: 200,
		weightVref: {
			minWeightLb: 6500, minWeightVref: 89, maxWeightLb: 10700, maxWeightVref: 113,
		},
	},
	"cj2+": {
		zfwLb: 8610,
		table: [
			[12500, 116], [11525, 111], [11300, 110], [11100, 109], [10900, 108], [10700, 107],
			[10500, 106], [10000, 104], [9500, 101], [9000, 99], [8500, 96], [8000, 93],
		],
		vneKt: 278,
		mmo: 0.737,
		vrKt: 110,
		vswKt: 95,
		vmxtKt: 25,
		vs0Kt: 88,
		vs1Kt: 102,
		vxKt: 124,
		vyKt: 191,
		minTdFt: 200,
		weightVref: {
			minWeightLb: 8000, minWeightVref: 93, maxWeightLb: 12500, maxWeightVref: 116,
		},
	},
	"cj3": {
		zfwLb: 9318,
		table: [
			[13870, 112], [12750, 108], [12600, 107], [12400, 106], [12200, 106], [12000, 105],
			[11500, 103], [11000, 101], [10500, 99], [10000, 97], [9500, 95], [9000, 93],
		],
		vneKt: 278,
		mmo: 0.737,
		vrKt: 105,
		vswKt: 93,
		vmxtKt: 25,
		vs0Kt: 80,
		vs1Kt: 95,
		vxKt: 110,
		vyKt: 160,
		minTdFt: 200,
		weightVref: {
			minWeightLb: 9000, minWeightVref: 93, maxWeightLb: 13870, maxWeightVref: 112,
		},
	},
	"cj3+": {
		zfwLb: 9318,
		table: [
			[13870, 112], [12750, 108], [12600, 107], [12400, 106], [12200, 106], [12000, 105],
			[11500, 103], [11000, 101], [10500, 99], [10000, 97], [9500, 95], [9000, 93],
		],
		vneKt: 278,
		mmo: 0.737,
		vrKt: 105,
		vswKt: 93,
		vmxtKt: 25,
		vs0Kt: 80,
		vs1Kt: 95,
		vxKt: 110,
		vyKt: 160,
		minTdFt: 200,
		weightVref: {
			minWeightLb: 9000, minWeightVref: 93, maxWeightLb: 13870, maxWeightVref: 112,
		},
	},
	"cj4": {
		zfwLb: 11014,
		table: [
			[16950, 118], [15500, 112], [15000, 110], [14500, 108], [14000, 106], [13500, 104],
			[13000, 102], [12000, 98], [11000, 94], [10500, 92],
		],
		vneKt: 305,
		mmo: 0.77,
		vrKt: 102,
		vswKt: 92,
		vmxtKt: 25,
		minTdFt: 200,
		weightVref: {
			minWeightLb: 10500, minWeightVref: 92, maxWeightLb: 16950, maxWeightVref: 118,
		},
	},
	"c501": {
		zfwLb: 8190,
		table: [[12500, 108], [7735, 85]],
		vneKt: 262,
		vrKt: 90,
		vswKt: 95,
		vmxtKt: 25,
		vs0Kt: 72,
		vs1Kt: 81,
		vxKt: 112,
		vyKt: 190,
		weightVref: {
			minWeightLb: 7735, minWeightVref: 85, maxWeightLb: 12500, maxWeightVref: 108,
		},
	},
	// Phenom 300 — NOT from the CJP guide: fitted against the Flysto ground-truth corpus
	// (scripts/fit-phenom-vref.ts; 10 weight-derived Vrefs, RMSE 1.35 kt). ZFW anchored
	// at the type's MAX zero-fuel weight (13,999 lb) — the conservative no-W&B convention
	// Flysto evidently uses: the fitted line then passes through the published AFM
	// Vref(FULL) ≈ 116 kt at MLW 17,042, and Flysto's own no-weight-data fallback is 118.
	// Table = fitted line Vref = 99.6 + 5.49·(GW − 13,999)/1000, MTOW 18,551 top row.
	"phenom300": {
		zfwLb: 13999,
		table: [
			[18551, 125], [17042, 116], [16500, 113], [16000, 111], [15500, 108], [15000, 105],
			[14500, 102], [14000, 100],
		],
		// No vneKt/mmo on purpose: no Phenom AFM in the corpus, and the Garmin logs cruise at a
		// derived M0.79–0.80, which is above the 300's book Mmo and at the 300E's. Set once the
		// variant and the AFM page are confirmed; until then the limit flags refuse for this model.
	},
};

/** Profile keys the seed defines — the suggestions offered when staff creates a profile. */
export const FOQA_SEEDED_PROFILE_KEYS = Object.keys(CJP_VREF_PROFILES).sort();
